import { useCallback, useEffect, useMemo, useState } from "react";
import {
  BookOpenText, Plus, CheckCircle, Archive, PencilSimple, Warning,
  DownloadSimple, MagnifyingGlass, Lock,
} from "@phosphor-icons/react";
import AdminShell from "@/pages/admin/AdminShell";
import { Section } from "@/pages/Shell";
import { formatError } from "@/lib/api";
import {
  listKnowledgeFacts, listKnowledgeDynamic, createKnowledgeFact,
  updateKnowledgeFact, approveKnowledgeFact, retireKnowledgeFact,
  bootstrapKnowledgeFacts,
} from "@/lib/knowledgeApi";

/**
 * Base de conocimiento — la pantalla donde se decide qué puede decir la IA.
 *
 * Filas operativas compactas, no tarjetas grandes: esto es una bandeja de
 * trabajo que se repasa, y una tarjeta por hecho obligaría a hacer scroll
 * para ver diez.
 *
 * Lo que la pantalla tiene que dejar claro de un vistazo, porque es lo que
 * se confunde: qué llega de verdad a la IA y qué no. Un hecho aprobado que
 * caducó se parece mucho a uno vigente si solo miras el estado, así que la
 * fila enseña el MOTIVO de bloqueo que calcula el backend, no una deducción
 * nuestra a partir del estado.
 */

const TAP = "min-h-[44px]";

const STATUS_STYLE = {
  approved: "bg-emerald-50 text-emerald-700 border-emerald-200",
  draft: "bg-amber-50 text-amber-700 border-amber-200",
  retired: "bg-stone-100 text-stone-500 border-stone-200",
};

function Pill({ children, className = "", ...rest }) {
  return (
    <span
      className={`inline-flex items-center gap-1 rounded-full border px-2 py-0.5 text-[11px] leading-tight ${className}`}
      {...rest}
    >
      {children}
    </span>
  );
}

function FactRow({ fact, busy, onEdit, onApprove, onRetire }) {
  const blocked = !fact.usable;
  return (
    <li
      className="border-b border-stone-200/70 last:border-b-0 py-3 px-3 sm:px-4"
      data-testid={`kb-row-${fact.fact_id}`}
    >
      <div className="flex flex-col sm:flex-row sm:items-start gap-2 sm:gap-4 min-w-0">
        <div className="min-w-0 flex-1">
          <div className="flex flex-wrap items-center gap-1.5 mb-1">
            <Pill className={STATUS_STYLE[fact.status] || STATUS_STYLE.draft}
                  data-testid={`kb-status-${fact.fact_id}`}>
              {fact.status_label}
            </Pill>
            <Pill className="bg-stone-50 text-stone-600 border-stone-200">
              {fact.category_label}
            </Pill>
            {fact.owner_confirmed && (
              <Pill className="bg-sky-50 text-sky-700 border-sky-200"
                    data-testid={`kb-owner-${fact.fact_id}`}
                    title="El dueño del producto ya decidió este dato y consta en PRODUCT_DECISIONS. Sigue necesitando aprobación: esta marca no aprueba nada.">
                confirmado por producto
              </Pill>
            )}
            {fact.sensitive && (
              <Pill className="bg-rose-50 text-rose-700 border-rose-200"
                    title="Categoría sensible: un hecho aprobado aquí respalda un dato, no autoriza a negociar ni a interpretar.">
                sensible
              </Pill>
            )}
            {blocked && (
              <Pill className="bg-rose-50 text-rose-700 border-rose-200"
                    data-testid={`kb-blocked-${fact.fact_id}`}>
                <Warning size={11} /> {fact.blocked_label}
              </Pill>
            )}
          </div>
          <div className="font-medium text-sm break-words">{fact.title}</div>
          <p className="text-sm text-stone-600 mt-0.5 break-words">{fact.content}</p>
          <div className="text-[11px] text-stone-500 mt-1.5 break-words">
            <span className="font-mono">{fact.ref}</span>
            {" · v"}{fact.version}
            {fact.source ? <> · <span data-testid={`kb-source-${fact.fact_id}`}>{fact.source}</span></> : null}
            {fact.updated_at ? ` · ${String(fact.updated_at).slice(0, 10)}` : ""}
          </div>
          {fact.conflicts_with && (
            <p className="text-[11px] text-rose-700 mt-1 break-words"
               data-testid={`kb-conflict-${fact.fact_id}`}>
              Conflicto: {fact.conflicts_with}
            </p>
          )}
        </div>

        <div className="flex flex-wrap gap-1.5 shrink-0">
          <button type="button" onClick={() => onEdit(fact)} disabled={busy}
            className={`dv-btn dv-btn-ghost text-xs inline-flex items-center gap-1 ${TAP} sm:min-h-0 disabled:opacity-40`}
            data-testid={`kb-edit-${fact.fact_id}`}>
            <PencilSimple size={13} /> Editar
          </button>
          {fact.status !== "approved" && fact.status !== "retired" && (
            <button type="button" onClick={() => onApprove(fact)} disabled={busy}
              className={`dv-btn dv-btn-primary text-xs inline-flex items-center gap-1 ${TAP} sm:min-h-0 disabled:opacity-40`}
              data-testid={`kb-approve-${fact.fact_id}`}>
              <CheckCircle size={13} /> Aprobar
            </button>
          )}
          {fact.status !== "retired" && (
            <button type="button" onClick={() => onRetire(fact)} disabled={busy}
              className={`dv-btn dv-btn-outline text-xs inline-flex items-center gap-1 ${TAP} sm:min-h-0 disabled:opacity-40`}
              data-testid={`kb-retire-${fact.fact_id}`}>
              <Archive size={13} /> Retirar
            </button>
          )}
        </div>
      </div>
    </li>
  );
}

const EMPTY_FORM = {
  category: "project", key: "", title: "", content: "", language: "es",
  customer_safe: true, source: "", source_reference: "",
};

function Editor({ fact, categories, onCancel, onSave, busy }) {
  const [form, setForm] = useState(() =>
    fact ? {
      category: fact.category, key: fact.key, title: fact.title,
      content: fact.content, language: fact.language || "es",
      customer_safe: !!fact.customer_safe, source: fact.source || "",
      source_reference: fact.source_reference || "",
    } : { ...EMPTY_FORM });
  const set = (k) => (e) =>
    setForm((f) => ({ ...f, [k]: e.target.type === "checkbox" ? e.target.checked : e.target.value }));

  return (
    <form
      className="dv-card mb-4"
      data-testid="kb-editor"
      onSubmit={(e) => { e.preventDefault(); onSave(form); }}
    >
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        <label className="text-xs min-w-0">
          <div className="text-[10px] uppercase tracking-widest text-stone-500 mb-1">Categoría</div>
          <select className="dv-input text-sm w-full" value={form.category}
                  onChange={set("category")} data-testid="kb-form-category">
            {categories.map((c) => (
              <option key={c.value} value={c.value}>{c.label}</option>
            ))}
          </select>
        </label>
        <label className="text-xs min-w-0">
          <div className="text-[10px] uppercase tracking-widest text-stone-500 mb-1">Clave</div>
          <input className="dv-input text-sm w-full" value={form.key} onChange={set("key")}
                 placeholder="leasehold_duracion" data-testid="kb-form-key" />
        </label>
        <label className="text-xs sm:col-span-2 min-w-0">
          <div className="text-[10px] uppercase tracking-widest text-stone-500 mb-1">Título</div>
          <input className="dv-input text-sm w-full" value={form.title} onChange={set("title")}
                 data-testid="kb-form-title" />
        </label>
        <label className="text-xs sm:col-span-2 min-w-0">
          <div className="text-[10px] uppercase tracking-widest text-stone-500 mb-1">
            Contenido — lo que la IA podrá afirmar, literalmente
          </div>
          <textarea className="dv-input text-sm w-full" rows={3} value={form.content}
                    onChange={set("content")} data-testid="kb-form-content" />
        </label>
        <label className="text-xs sm:col-span-2 min-w-0">
          <div className="text-[10px] uppercase tracking-widest text-stone-500 mb-1">
            Procedencia — sin esto no se puede aprobar
          </div>
          <input className="dv-input text-sm w-full" value={form.source} onChange={set("source")}
                 placeholder="Quién lo dijo y cuándo" data-testid="kb-form-source" />
        </label>
        <label className="text-xs min-w-0">
          <div className="text-[10px] uppercase tracking-widest text-stone-500 mb-1">Idioma</div>
          <select className="dv-input text-sm w-full" value={form.language}
                  onChange={set("language")} data-testid="kb-form-language">
            <option value="es">Español</option>
            <option value="en">Inglés</option>
            <option value="any">Cualquiera</option>
          </select>
        </label>
        <label className={`text-xs inline-flex items-center gap-2 ${TAP} sm:min-h-0`}>
          <input type="checkbox" checked={form.customer_safe} onChange={set("customer_safe")}
                 data-testid="kb-form-customer-safe" />
          <span>Se le puede decir a un cliente</span>
        </label>
      </div>
      <div className="flex flex-wrap gap-2 mt-3">
        <button type="submit" disabled={busy}
          className={`dv-btn dv-btn-primary text-sm ${TAP} sm:min-h-0 disabled:opacity-40`}
          data-testid="kb-form-save">Guardar</button>
        <button type="button" onClick={onCancel}
          className={`dv-btn dv-btn-ghost text-sm ${TAP} sm:min-h-0`}
          data-testid="kb-form-cancel">Cancelar</button>
      </div>
      <p className="text-[11px] text-stone-500 mt-2">
        Guardar un cambio de contenido devuelve el hecho a borrador: hay que
        volver a aprobarlo antes de que la IA pueda decirlo.
      </p>
    </form>
  );
}

export default function KnowledgeBasePage() {
  const [items, setItems] = useState([]);
  const [dynamic, setDynamic] = useState([]);
  const [categories, setCategories] = useState([]);
  const [statuses, setStatuses] = useState([]);
  const [fCategory, setFCategory] = useState("");
  const [fStatus, setFStatus] = useState("");
  const [q, setQ] = useState("");
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [notice, setNotice] = useState("");
  const [busyId, setBusyId] = useState(null);
  const [editing, setEditing] = useState(null); // fact | "new" | null

  const reload = useCallback(async () => {
    setLoading(true);
    try {
      const res = await listKnowledgeFacts({ category: fCategory, status: fStatus, q });
      setItems(res.items);
      if (res.categories.length) setCategories(res.categories);
      if (res.statuses.length) setStatuses(res.statuses);
      setError("");
    } catch (e) {
      setError(formatError(e));
    } finally {
      setLoading(false);
    }
  }, [fCategory, fStatus, q]);

  useEffect(() => { reload(); }, [reload]);

  useEffect(() => {
    let alive = true;
    listKnowledgeDynamic()
      .then((rows) => { if (alive) setDynamic(rows); })
      .catch(() => { if (alive) setDynamic([]); });
    return () => { alive = false; };
  }, []);

  const usableCount = useMemo(() => items.filter((i) => i.usable).length, [items]);

  const act = async (id, fn, msg) => {
    setBusyId(id);
    setNotice("");
    try {
      await fn();
      setNotice(msg);
      await reload();
    } catch (e) {
      setError(formatError(e));
    } finally {
      setBusyId(null);
    }
  };

  const onSave = async (form) => {
    const isNew = editing === "new";
    await act(isNew ? "new" : editing.fact_id, async () => {
      if (isNew) await createKnowledgeFact(form);
      else await updateKnowledgeFact(editing.fact_id, form);
      setEditing(null);
    }, isNew ? "Hecho creado en borrador." : "Hecho actualizado.");
  };

  return (
    <AdminShell>
      <div className="mb-6 fade-up">
        <div className="text-xs tracking-[0.3em] uppercase text-stone-500 mb-1">IA comercial</div>
        <h1 className="font-serif-dv text-4xl">Base de conocimiento.</h1>
        <p className="text-stone-500 mt-2">
          Lo único que la IA puede afirmar sobre Dolce Villas. Un hecho llega a
          la IA cuando está aprobado, es apto para cliente, está vigente y tiene
          procedencia — no por existir.
        </p>
      </div>

      <div className="flex flex-wrap gap-2 items-center mb-4">
        <button type="button" onClick={() => setEditing("new")}
          className={`dv-btn dv-btn-primary text-sm inline-flex items-center gap-1.5 ${TAP} sm:min-h-0`}
          data-testid="kb-create">
          <Plus size={14} /> Nuevo hecho
        </button>
        <button type="button"
          onClick={() => act("bootstrap", bootstrapKnowledgeFacts,
            "Candidatos cargados como borrador. Ninguno llega a la IA hasta que lo apruebes.")}
          disabled={busyId === "bootstrap"}
          className={`dv-btn dv-btn-ghost text-sm inline-flex items-center gap-1.5 ${TAP} sm:min-h-0 disabled:opacity-40`}
          data-testid="kb-bootstrap"
          title="Crea como BORRADOR los candidatos con procedencia conocida que aún no existan. No pisa nada.">
          <DownloadSimple size={14} /> Cargar candidatos
        </button>
        <span className="text-xs text-stone-500" data-testid="kb-usable-count">
          {usableCount} de {items.length} llegan a la IA
        </span>
      </div>

      {error && (
        <div className="dv-card border-rose-200 bg-rose-50 text-rose-800 text-sm mb-4" data-testid="kb-error">
          {error}
        </div>
      )}
      {notice && (
        <div className="dv-card border-emerald-200 bg-emerald-50 text-emerald-800 text-sm mb-4" data-testid="kb-notice">
          {notice}
        </div>
      )}

      <Section
        title="Fuentes canónicas"
        subtitle="Datos que ya tienen dueño en el producto. Se leen en cada análisis y no se editan aquí."
      >
        <div className="dv-card">
          {dynamic.length === 0 ? (
            <p className="text-sm text-stone-500" data-testid="kb-dynamic-empty">
              Ninguna fuente canónica tiene datos publicados ahora mismo.
            </p>
          ) : (
            <ul className="divide-y divide-stone-200/70" data-testid="kb-dynamic-list">
              {dynamic.map((d) => (
                <li key={d.key} className="py-3 first:pt-0 last:pb-0 min-w-0"
                    data-testid={`kb-dynamic-${d.key}`}>
                  <div className="flex flex-wrap items-center gap-1.5 mb-1">
                    <Pill className="bg-sky-50 text-sky-700 border-sky-200">
                      <Lock size={11} /> Fuente dinámica
                    </Pill>
                    <span className="text-[11px] text-stone-500 break-words">{d.owner}</span>
                  </div>
                  <div className="font-medium text-sm break-words">{d.title}</div>
                  <p className="text-sm text-stone-600 break-words">{d.content}</p>
                  <div className="text-[11px] text-stone-500 mt-1 font-mono break-words">{d.ref}</div>
                </li>
              ))}
            </ul>
          )}
        </div>
      </Section>

      <Section title="Hechos aprobados" subtitle="Busca, filtra, edita, aprueba o retira.">
        <div className="dv-card">
          <div className="flex flex-wrap gap-2 items-end mb-3">
            <label className="text-xs flex-1 min-w-[140px]">
              <div className="text-[10px] uppercase tracking-widest text-stone-500 mb-1">Buscar</div>
              <div className="relative">
                <MagnifyingGlass size={14}
                  className="absolute left-2 top-1/2 -translate-y-1/2 text-stone-400" />
                <input className="dv-input text-sm w-full pl-7" value={q}
                       onChange={(e) => setQ(e.target.value)}
                       placeholder="leasehold, precio…" data-testid="kb-search" />
              </div>
            </label>
            <label className="text-xs min-w-0">
              <div className="text-[10px] uppercase tracking-widest text-stone-500 mb-1">Categoría</div>
              <select className="dv-input text-sm" value={fCategory}
                      onChange={(e) => setFCategory(e.target.value)} data-testid="kb-filter-category">
                <option value="">Todas</option>
                {categories.map((c) => <option key={c.value} value={c.value}>{c.label}</option>)}
              </select>
            </label>
            <label className="text-xs min-w-0">
              <div className="text-[10px] uppercase tracking-widest text-stone-500 mb-1">Estado</div>
              <select className="dv-input text-sm" value={fStatus}
                      onChange={(e) => setFStatus(e.target.value)} data-testid="kb-filter-status">
                <option value="">Todos</option>
                {statuses.map((s) => <option key={s.value} value={s.value}>{s.label}</option>)}
              </select>
            </label>
          </div>

          {editing && (
            <Editor
              fact={editing === "new" ? null : editing}
              categories={categories.length ? categories : [{ value: "project", label: "Proyecto" }]}
              busy={busyId != null}
              onCancel={() => setEditing(null)}
              onSave={onSave}
            />
          )}

          {loading ? (
            <p className="text-sm text-stone-500" data-testid="kb-loading">Cargando…</p>
          ) : items.length === 0 ? (
            <div className="text-sm text-stone-500 py-6 text-center" data-testid="kb-empty">
              <BookOpenText size={22} className="mx-auto mb-2 text-stone-400" />
              No hay hechos con estos filtros. Mientras no haya ninguno aprobado,
              todo dato del proyecto sigue siendo desconocido para la IA.
            </div>
          ) : (
            <ul className="-mx-3 sm:-mx-4" data-testid="kb-list">
              {items.map((f) => (
                <FactRow key={f.fact_id} fact={f} busy={busyId === f.fact_id}
                  onEdit={setEditing}
                  onApprove={(x) => act(x.fact_id, () => approveKnowledgeFact(x.fact_id),
                                        "Hecho aprobado. La IA ya puede afirmarlo.")}
                  onRetire={(x) => act(x.fact_id, () => retireKnowledgeFact(x.fact_id),
                                       "Hecho retirado. Se conserva para la auditoría.")}
                />
              ))}
            </ul>
          )}
        </div>
      </Section>
    </AdminShell>
  );
}
